<template>
  <section v-if="extraInformation.length || canEdit" class="poster-info">
    <h3 v-if="extraInformation.length" class="poster-info-heading">{{ t('generalInfo') }}</h3>
    <ul v-if="extraInformation.length" class="poster-info-list">
      <li
        v-for="(item, index) in extraInformation"
        :key="item.id || index"
        :class="['poster-info-item', { 'poster-info-open': isOpen(index) }]"
      >
        <button
          type="button"
          class="poster-info-row"
          :aria-expanded="isOpen(index) ? 'true' : 'false'"
          @click="toggle(index)"
        >
          <span class="poster-info-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="18" height="18">
              <path fill="currentColor" d="M11 7h2v2h-2zm0 4h2v6h-2zm1-9a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm0 18a8 8 0 1 1 0-16 8 8 0 0 1 0 16z"/>
            </svg>
          </span>
          <span class="poster-info-title">{{ item.title }}</span>
          <svg class="poster-info-chevron" viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
            <path fill="currentColor" d="M7.4 8.6 12 13.2l4.6-4.6L18 10l-6 6-6-6z"/>
          </svg>
        </button>
        <div v-if="isOpen(index)" class="poster-info-body">
          <!-- Above the text, so a long note does not hide it. -->
          <button
            v-if="canEdit"
            type="button"
            class="poster-info-edit"
            @click="editExtraInformation(item.id)"
          >{{ t('edit') }}</button>
          <!-- The same markup CollapseText shows in the classic design. -->
          <div class="poster-info-text" v-html="item.text"></div>
        </div>
      </li>
    </ul>
    <div v-if="canEdit" class="poster-info-add-wrap">
      <button type="button" class="poster-info-add" @click="openExtraInformationForRide(null)">
        {{ t('addRideGeneralInfo') }} <span aria-hidden="true">+</span>
      </button>
    </div>
  </section>
</template>

<script>
import { translationMixin } from "@/mixins/translationMixin";
import { rideGeneralInfoMixin } from "@/components/RideSummary/rideGeneralInfoMixin";

/**
 * The poster design's general information: one row per note showing only its
 * title, opened in place to read it. Several can be open at once. Add / edit
 * for the ride's editors, as in the classic design (rideGeneralInfoMixin).
 */
export default {
  name: 'PosterGeneralInfo',
  mixins: [translationMixin, rideGeneralInfoMixin],
  props: {
    ride: { type: Object, required: true },
  },
  data() {
    return { open: [] };
  },
  methods: {
    isOpen(index) {
      return this.open.includes(index);
    },
    toggle(index) {
      this.open = this.isOpen(index)
        ? this.open.filter(i => i !== index)
        : this.open.concat(index);
    },
  },
};
</script>

<style scoped>
.poster-info {
  max-width: 760px;
  margin: 8px auto 36px;
}

.poster-info-heading {
  margin: 0 4px 14px;
  font-size: 24px;
  font-weight: 700;
  color: #1d1d1f;
  text-align: start;
}

.poster-info-list {
  margin: 0;
  padding: 0;
  list-style: none;
  overflow: hidden;
  border-radius: 20px;
  background: #fff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06), 0 8px 24px -16px rgba(0, 0, 0, 0.35);
}

.poster-info-item + .poster-info-item {
  border-top: 1px solid #ececef;
}

.poster-info-row {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 16px 18px;
  border: 0;
  background: none;
  color: inherit;
  font: inherit;
  text-align: start;
  cursor: pointer;
}

.poster-info-icon {
  flex: 0 0 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 10px;
  background: #f2f2f4;
  color: var(--primary-color, #07A0C3);
}

.poster-info-title {
  flex: 1;
  min-width: 0;
  font-size: 17px;
  font-weight: 600;
  line-height: 1.35;
  color: #1d1d1f;
}

.poster-info-chevron {
  flex: 0 0 20px;
  color: #8e8e93;
  transition: transform 0.2s ease;
}

.poster-info-open .poster-info-chevron {
  transform: rotate(180deg);
}

.poster-info-body {
  padding: 0 18px 18px;
  padding-inline-start: 64px;
}

/* The editor's own markup: one size for all of it, whatever it carries. */
.poster-info-text,
.poster-info-text ::v-deep * {
  font-size: 15px !important;
  line-height: 24px !important;
  color: #3a3a3c;
}

.poster-info-text {
  white-space: pre-wrap;
  overflow-wrap: break-word;
}

.poster-info-text ::v-deep a {
  color: var(--primary-color, #07A0C3);
  text-decoration: underline;
}

.poster-info-text ::v-deep img {
  max-width: 100%;
  height: auto;
  border-radius: 12px;
}

.poster-info-edit,
.poster-info-add {
  border: 0;
  border-radius: 999px;
  background: none;
  box-shadow: inset 0 0 0 1.5px var(--primary-color, #07A0C3);
  color: var(--primary-color, #07A0C3);
  font-weight: 600;
  cursor: pointer;
}

.poster-info-edit {
  margin-bottom: 10px;
  padding: 6px 16px;
  font-size: 14px;
}

.poster-info-add-wrap {
  display: flex;
  justify-content: center;
  margin-top: 16px;
}

.poster-info-add {
  padding: 10px 22px;
  font-size: 16px;
}
</style>
